import { z } from 'zod'
import { ehMotivoSaida, type MotivoSaida } from './avisoDeLogin'

/**
 * Coordenação de sessão entre abas do MESMO navegador (D9). Ver `sessaoEntreAbas.md`.
 *
 * Prazo do anúncio: pré-espera curta. Quem garante que nenhuma aba renova durante troca/saída é o
 * trinco (`trinco.ts`); passado o prazo, quem espera segue pro trinco e espera lá. Anúncio órfão
 * (aba fechada no meio da operação) não trava as outras.
 */
export const PRAZO_OPERACAO_MS = 10_000
export const CHAVE_OPERACAO = 'auth:operacao'
/** Fora de `AUTH_KEYS` de propósito: sobrevive ao `clearAuthStorage` — quem apaga grava antes, a outra aba lê depois. */
export const CHAVE_MOTIVO = 'auth:motivo_saida'

const OperacaoSchema = z.object({
  /** Só `retirarOperacao` desta aba apaga ESTE anúncio — a saída de uma aba nunca apaga a operação de outra. */
  id: z.string(),
  tipo: z.enum(['troca-senha', 'sair', 'sair-todos']),
  /** Epoch em MILISSEGUNDOS. */
  expira_em: z.number().finite(),
})

export type Operacao = z.infer<typeof OperacaoSchema>
export type TipoDeOperacao = Operacao['tipo']

let minhaOperacao: string | null = null
/**
 * ESTA aba entrou em `encerrarSessaoLocal` e já navega pro login. Nunca volta a `false`:
 * vale enquanto o documento vive, e a navegação completa o descarta junto com o módulo.
 */
let saidaDestaAba = false
/** O evento `storage` não dispara na aba que escreveu: quem espera nesta aba é acordado por aqui. */
const ouvintesLocais = new Set<() => void>()

function lerOperacao(): Operacao | null {
  const bruto = localStorage.getItem(CHAVE_OPERACAO)
  if (bruto === null) return null
  let json: unknown
  try {
    json = JSON.parse(bruto)
  } catch {
    // Valor que não é JSON (versão antiga, adulterado): não há anúncio válido. Tratado
    // como ausência — o mesmo que o Zod faz com a forma errada logo abaixo.
    return null
  }
  const lido = OperacaoSchema.safeParse(json)
  return lido.success ? lido.data : null
}

export function lerOperacaoEmVigor(agora: number = Date.now()): Operacao | null {
  const op = lerOperacao()
  return op !== null && op.expira_em > agora ? op : null
}

export function anunciarOperacao(tipo: TipoDeOperacao): void {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  minhaOperacao = id
  const op: Operacao = { id, tipo, expira_em: Date.now() + PRAZO_OPERACAO_MS }
  localStorage.setItem(CHAVE_OPERACAO, JSON.stringify(op))
}

export function retirarOperacao(): void {
  if (minhaOperacao !== null && lerOperacao()?.id === minhaOperacao) {
    localStorage.removeItem(CHAVE_OPERACAO)
  }
  minhaOperacao = null
  for (const acordar of [...ouvintesLocais]) acordar()
}

/** Resolve quando não houver anúncio em vigor. Nunca rejeita. Teto: `PRAZO_OPERACAO_MS`. */
export function esperarFimDaOperacao(): Promise<void> {
  const op = lerOperacaoEmVigor()
  if (op === null) return Promise.resolve()
  return new Promise((resolve) => {
    const terminar = () => {
      clearTimeout(prazo)
      window.removeEventListener('storage', aoMudarStorage)
      ouvintesLocais.delete(conferir)
      resolve()
    }
    const conferir = () => {
      if (lerOperacaoEmVigor() === null) terminar()
    }
    const aoMudarStorage = (e: StorageEvent) => {
      if (e.key === CHAVE_OPERACAO) conferir()
    }
    // Teto no prazo: `expira_em` adulterado (ou de relógio torto) não prende a aba além
    // do que uma operação real duraria.
    // `terminar` só roda depois desta linha: pelo timer ou pelos ouvintes armados abaixo.
    const prazo = setTimeout(terminar, Math.min(Math.max(op.expira_em - Date.now(), 0), PRAZO_OPERACAO_MS))
    window.addEventListener('storage', aoMudarStorage)
    ouvintesLocais.add(conferir)
  })
}

/**
 * Marca que ESTA aba está saindo (I8): um pedido dela ainda em voo que leve 401 (o "Sair"
 * revogou o jti) não pode tratar o token sumido como "Você saiu em outra aba.".
 */
export function marcarSaidaDestaAba(): void {
  saidaDestaAba = true
}

export function estaAbaEstaSaindo(): boolean {
  return saidaDestaAba
}

/** Só para teste: o módulo sobrevive entre casos do mesmo arquivo. */
export function __resetSaidaDestaAba(): void {
  saidaDestaAba = false
}

export function gravarMotivoSaida(motivo: MotivoSaida): void {
  localStorage.setItem(CHAVE_MOTIVO, motivo)
}

/** Ausente ou desconhecido → `'encerrada'` (spec §2: sem motivo gravado, "sessão encerrada"). */
export function lerMotivoSaida(): MotivoSaida {
  const valor = localStorage.getItem(CHAVE_MOTIVO)
  return ehMotivoSaida(valor) ? valor : 'encerrada'
}

export function limparMotivoSaida(): void {
  localStorage.removeItem(CHAVE_MOTIVO)
}

/**
 * Motivo que alguma aba gravou ao sair, ou `null` se ninguém saiu desde o último login (que apaga o
 * motivo). É o "outra aba sinalizou saída" da faixa 25 §4 / I8: a aba que levou `REFRESH_INVALID` segue o
 * motivo dela em vez de dizer "sessão encerrada".
 */
export function lerMotivoGravado(): MotivoSaida | null {
  const valor = localStorage.getItem(CHAVE_MOTIVO)
  return ehMotivoSaida(valor) ? valor : null
}

/**
 * "Sair" que o back não confirmou (faixa 25 §6, D5): o cookie de renovação é HttpOnly e só o back o
 * apaga — sem a marca, o próximo `/login` o renovaria e reabriria a sessão. É MARCA, não credencial (o
 * valor é o instante em ms). Fora de `AUTH_KEYS`: sobrevive ao `clearAuthStorage`.
 */
export const CHAVE_SAIDA_PENDENTE = 'auth:saida_pendente'

export function marcarSaidaPendente(): void {
  localStorage.setItem(CHAVE_SAIDA_PENDENTE, String(Date.now()))
}


export function haSaidaPendente(): boolean {
  return localStorage.getItem(CHAVE_SAIDA_PENDENTE) !== null
}

export function limparSaidaPendente(): void {
  localStorage.removeItem(CHAVE_SAIDA_PENDENTE)
}
